import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from '../../../components/common/Modal';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

const ExitForm = ({ isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    // Asset Return
    assets_returned: false,
    
    // Confidentiality
    confidential_documents_returned: false,
    confidentiality_agreement: false,
    
    // Employee Details
    department: '',
    designation: '',
    
    // Feedback
    feedback: '',
    
    // Declarations
    information_accuracy: false,
    legal_consequences: false,
    
    // Working Details
    last_working_day: '',
    served_notice_period: '',
    
    // Manager Details
    manager_name: '',
    manager_signature: '',
    
    // Employee Details
    employee_signature: 'Digital Signature',
    reason_for_leaving: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resignation, setResignation] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchResignation();
    } else {
      // Reset form when modal is closed
      setForm({
        assets_returned: false,
        confidential_documents_returned: false,
        confidentiality_agreement: false,
        department: '',
        designation: '',
        feedback: '',
        information_accuracy: false,
        legal_consequences: false,
        last_working_day: '',
        served_notice_period: '',
        manager_name: '',
        manager_signature: '',
        employee_signature: 'Digital Signature',
        reason_for_leaving: ''
      });
      setResignation(null);
    }
  }, [isOpen]);

  const fetchResignation = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/employee/resignation/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.data && response.data.length > 0) {
        const latestResignation = response.data[0];
        if (latestResignation.status === 'APPROVED') {
          setResignation(latestResignation);
          // Pre-fill some fields from resignation data
          setForm(prev => ({
            ...prev,
            last_working_day: latestResignation.last_working_day,
            reason_for_leaving: latestResignation.reason_for_leaving,
            department: latestResignation.department || '',
            designation: latestResignation.designation || '',
            served_notice_period: latestResignation.served_notice_period || '',
            manager_name: latestResignation.manager_name || '',
            manager_signature: latestResignation.manager_signature || ''
          }));
        } else {
          toast.error('You can only fill the exit form for approved resignations');
          onClose();
        }
      }
    } catch (error) {
      console.error('Error fetching resignation:', error);
      toast.error('Failed to fetch resignation details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Prepare the data in the correct format
      const formData = {
        ...form,
        status: 'EXITED',  // Set status to EXITED
        employee_signature_date: new Date().toISOString().split('T')[0],  // Current date
        manager_signature_date: new Date().toISOString().split('T')[0],    // Current date
        assets_returned: form.assets_returned ? 'Yes' : 'No',  // Convert boolean to Yes/No
        confidential_documents_returned: form.confidential_documents_returned ? 'Yes' : 'No',
        confidentiality_agreement: form.confidentiality_agreement ? 'Yes' : 'No',
        information_accuracy: form.information_accuracy ? 'Yes' : 'No',
        legal_consequences: form.legal_consequences ? 'Yes' : 'No'
      };

      const response = await axios.post(`${API_BASE}/api/employee/resignation/${resignation.id}/exit-form/`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'EXITED') {
        toast.success('Exit form submitted successfully');
        if (onSuccess) {
          onSuccess(response.data);
        }
        onClose();
      } else {
        toast.error('Failed to update status to EXITED');
      }
    } catch (error) {
      console.error('Error submitting exit form:', error);
      toast.error(error.response?.data?.error || 'Failed to submit exit form');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Exit Form">
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        </div>
      ) : !resignation ? (
        <div className="text-center py-8 text-gray-500">No approved resignation found</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Asset Return Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Asset Return</h3>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="assets_returned"
                  checked={form.assets_returned}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Did you return your assets?</span>
              </label>
            </div>
          </div>

          {/* Confidentiality Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Confidentiality</h3>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="confidential_documents_returned"
                  checked={form.confidential_documents_returned}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Did you return all confidential documents?</span>
              </label>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="confidentiality_agreement"
                  checked={form.confidentiality_agreement}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Did you sign the confidentiality agreement?</span>
              </label>
            </div>
          </div>

          {/* Employee Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Employee Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Department</label>
                <input
                  type="text"
                  name="department"
                  value={form.department}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Designation</label>
                <input
                  type="text"
                  name="designation"
                  value={form.designation}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Working Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Working Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Working Day</label>
                <input
                  type="date"
                  name="last_working_day"
                  value={form.last_working_day}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notice Period Served (Days)</label>
                <input
                  type="number"
                  name="served_notice_period"
                  value={form.served_notice_period}
                  onChange={handleChange}
                  required
                  min="0"
                  disabled={submitting}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Manager Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Manager Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Manager Name</label>
                <input
                  type="text"
                  name="manager_name"
                  value={form.manager_name}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Manager Signature</label>
                <input
                  type="text"
                  name="manager_signature"
                  value={form.manager_signature}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Reason for Leaving */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Reason for Leaving</h3>
            <div>
              <textarea
                name="reason_for_leaving"
                value={form.reason_for_leaving}
                onChange={handleChange}
                required
                rows="3"
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Feedback Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Feedback</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Your Feedback</label>
              <textarea
                name="feedback"
                value={form.feedback}
                onChange={handleChange}
                rows={4}
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Declarations Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Declarations</h3>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="information_accuracy"
                  checked={form.information_accuracy}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">I confirm that all information provided is accurate</span>
              </label>
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="legal_consequences"
                  checked={form.legal_consequences}
                  onChange={handleChange}
                  required
                  disabled={submitting}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">I understand the legal consequences of providing false information</span>
              </label>
            </div>
          </div>

          {/* Employee Signature */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Employee Signature</h3>
            <div>
              <input
                type="text"
                name="employee_signature"
                value={form.employee_signature}
                onChange={handleChange}
                required
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </div>
              ) : (
                'Submit Exit Form'
              )}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ExitForm; 